import { Box, Flex, Spacing, Table, Text } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { memo, useMemo } from 'react';
import { NetSuiteMatchingLine, useNetSuiteLineFieldSettings } from 'shared/hooks/(matchings)';
import { useNetSuiteCompanyCustomFields, useNetSuiteNewLineCustomFieldsDefinitions } from 'shared/hooks/(requests)';

import { getColumnDefinitions } from '../../NetSuiteMatchingPopup.config';
import { netSuiteSourceMatchingLineState } from '../../NetSuiteMatchingPopup.states';
import { NetSuiteSourceLine } from '../../NetSuiteMatchingPopup.types';
import { messages } from './SourceLineInformation.messages';
import { SourceLineInformationProps } from './SourceLineInformation.types';

export const SourceLineInformation = memo((props: SourceLineInformationProps) => {
    const { request } = props;

    const sourceLine = useAtomValue(netSuiteSourceMatchingLineState);

    const { netSuiteCompanyCustomFields } = useNetSuiteCompanyCustomFields(request.companyId);

    const fieldSettings = useNetSuiteLineFieldSettings(
        request.companyId,
        request.integrationCode,
        sourceLine?.lineType
    );

    const tableData = useMemo(() => {
        if (!sourceLine) {
            return [];
        }

        return [
            {
                ...sourceLine,
                id: 'selected-line',
                units: 'units' in sourceLine ? sourceLine.units : null,
                item: 'item' in sourceLine ? sourceLine.item : null,
                category: 'category' in sourceLine ? sourceLine.category : null,
                account: 'account' in sourceLine ? sourceLine.account : null,
            },
        ];
    }, [sourceLine]);

    const customFieldsColumnDefinitions = useNetSuiteNewLineCustomFieldsDefinitions<
        NetSuiteSourceLine | NetSuiteMatchingLine
    >(tableData, netSuiteCompanyCustomFields);

    const columnDefinitions = useMemo(
        () =>
            getColumnDefinitions({
                fieldSettings,
                customFieldsColumnDefinitions,
                lineType: sourceLine?.lineType,
            }),
        [sourceLine, fieldSettings, customFieldsColumnDefinitions]
    );

    if (!tableData.length) {
        return null;
    }

    return (
        <Box spacing='16 24 0' color='midnight20' radius='small' width='100%'>
            <Flex direction='column'>
                <Text font='headline' fontSize='xxsmall' fontWeight='medium' color='midnight80'>
                    {messages.title}
                </Text>

                <Spacing height={16} />

                <Table
                    columns={columnDefinitions}
                    data={tableData}
                    hideBorderLastRow
                    headerColor='transparent'
                    hideEmptyColumns
                    hoverable={false}
                />
            </Flex>
        </Box>
    );
});

SourceLineInformation.displayName = 'SourceLineInformation';
